/**
 * This HOC is duplicate of `authZeroScreen.js` with different implementation for AWS Cognito.
 */

import React from 'react';
import { connect } from 'react-redux';
import { withNavigationFocus } from 'react-navigation';

import login from 'modules/general/shared/action/login';
import logout from 'modules/general/shared/action/logout';
import getBusUserConfig from 'modules/bus/shared/actions/getBusUserConfig';
import isUserLoggedIn from 'modules/general/shared/utils/isUserLoggedIn';
import withAuth from './withAuth';
import hoistNonReactStatics from '../utils/hoistNonReactStatics';
import busProviderId from 'modules/bus/shared/selectors/userConfig/busProviderId';

import { TOS_APP_PERMISSION } from 'common/constants/authPermissionConstants';
import permissionRequiredAll from 'common/utils/validation/permissionRequiredAll';
import AuthorizeView from 'common/components/AuthorizeView/AuthorizeView';
import getBusControlSettings from 'modules/bus/shared/actions/getBusControlSettings';

// TODO (yusei) find way to decouple this with product specific user config
export default function cognitoScreen(Screen, permission = []) {
  const componentName = Screen.displayName || Screen.name || 'unknown';

  const mapStateToProps = state => ({
    authState: state.general.auth,
    busUserConfigInitialized: state.bus.userConfig.initialized,
    busControlSettingsInitialized: state.bus.controlSettings.initialized,
    providerId: busProviderId(state),
  });

  const mapDispatchToProps = dispatch => ({
    login: authResult => dispatch(login(authResult)),
    logout: () => dispatch(logout()),
    getBusUserConfig: () => dispatch(getBusUserConfig()),
    getBusControlSettingsData: () => dispatch(getBusControlSettings()),
  });

  const connectToRedux = connect(
    mapStateToProps,
    mapDispatchToProps
  );

  @withAuth
  @connectToRedux
  @withNavigationFocus
  @hoistNonReactStatics(Screen)
  class AuthAwareComponent extends React.Component {
    displayName = `cognitoScreen(${componentName})`;
    updateCount = 0;

    componentDidUpdate(prevProps) {
      this.updateCount = this.updateCount + 1;

      // since we use only client navigation, all routes start from 'Home',
      // so we need to make sure to not trigger auth check if user enter from
      // page without this wrapper (such as login & callback)
      if (
        this.props.navigation.state.routeName === 'Home' &&
        window.location.pathname !== '/'
      ) {
        return;
      }

      // only check on first update or when transition to focused screen
      // componentDidMount is not really reliable as component does not
      // necessary umounted after navigated to other pages
      const firstUpdate = this.updateCount <= 1;
      const transitionToFocused = this.props.isFocused && !prevProps.isFocused;
      if (firstUpdate || transitionToFocused) {
        this.check();
      }
    }

    check = async () => {
      const {
        authState,
        isFocused,
        userPermission,
        busUserConfigInitialized,
        getBusUserConfig,
        busControlSettingsInitialized,
        getBusControlSettingsData,
      } = this.props;

      if (!isFocused) {
        // not focused screen
        return;
      }

      if (!isUserLoggedIn(authState)) {
        this.navigateToLogin();
        return;
      }

      const isAuthorizedUser =
        userPermission &&
        permissionRequiredAll({
          userPermission,
          requiredPermission: [TOS_APP_PERMISSION],
        });

      if (!isAuthorizedUser) {
        this.navigateToLogin({ unauthorized: true });
        return;
      }

      clearTimeout(this._authTimeout);

      // get all user config
      if (!busUserConfigInitialized) {
        await getBusUserConfig();
      }

      // force user to choose provider if undefined
      if (
        !this.props.providerId &&
        this.props.navigation.state.routeName !== 'SwitchProvider'
      ) {
        this.navigateToSwitchProvider();
      } else {
        if (!busControlSettingsInitialized) await getBusControlSettingsData();
      }
    };

    registerAuthState = async getAuth => {
      try {
        const authResult = await getAuth();

        this.props.login(authResult);
      } catch (err) {
        // failed to login
        this.navigateToLogin();
      }
    };

    navigateToLogin = params => {
      // clear all auth token and user config state on redux
      this.props.logout();

      // use small amount of timeout to prevent race condition with
      // initial navigation to home (at homepage) by react-navigation
      // default behaviour
      setTimeout(() => this.props.navigation.push('Login', params), 100);
    };

    navigateToSwitchProvider = () => {
      // use small amount of timeout to prevent race condition with
      // initial navigation to home (at homepage) by react-navigation
      // default behaviour
      setTimeout(() => this.props.navigation.push('SwitchProvider'), 100);
    };

    render() {
      return (
        <AuthorizeView.RequiredAll requiredPermission={permission}>
          {allowed => allowed && <Screen {...this.props} />}
        </AuthorizeView.RequiredAll>
      );
    }
  }

  return AuthAwareComponent;
}
